import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type CreatePolicyRequest, type UpdatePolicyRequest } from '../lib/api'
import * as Sentry from '@sentry/tanstackstart-react'

export const policyKeys = {
  all: ['policies'] as const,
  lists: () => [...policyKeys.all, 'list'] as const,
  list: () => [...policyKeys.lists()] as const,
  details: () => [...policyKeys.all, 'detail'] as const,
  detail: (id: string) => [...policyKeys.details(), id] as const,
  preset: () => [...policyKeys.all, 'preset'] as const,
}

export function usePolicies() {
  return useQuery({
    queryKey: policyKeys.list(),
    queryFn: () => api.getPolicies(),
  })
}

export function usePolicy(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: policyKeys.detail(id),
    queryFn: () => api.getPolicy(id),
    enabled: options?.enabled !== false && !!id,
  })
}

export function usePresetPolicy() {
  return useQuery({
    queryKey: policyKeys.preset(),
    queryFn: () => api.getPresetPolicy(),
  })
}

export function useCreatePolicy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreatePolicyRequest) => api.createPolicy(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.list() })
    },
    onError: (err) => Sentry.captureException(err),
  })
}

export function useUpdatePolicy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePolicyRequest }) =>
      api.updatePolicy(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: policyKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: policyKeys.list() })
    },
    onError: (err) => Sentry.captureException(err),
  })
}

export function useDeletePolicy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.deletePolicy(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: policyKeys.list() })
    },
    onError: (err) => Sentry.captureException(err),
  })
}
